import { useState, useEffect, useRef, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Clapperboard, Loader2, Download, RotateCcw } from "lucide-react";
import { animateFrame, getVideoJob } from "@/services/videoService";

// Google Veo 3.1 supports 16:9 / 9:16 and a fixed 8s clip.
const ASPECTS = ["16:9", "9:16"];
const DURATIONS = [8];

/**
 * Animate a rendered storyboard frame into a short clip (image-to-video).
 * Submits a job, polls until ready, and plays the result inline.
 */
export default function AnimateFrameModal({ open, onOpenChange, frame, projectId, imageUrl }) {
  const [prompt, setPrompt] = useState("");
  const [duration, setDuration] = useState(8);
  const [aspect, setAspect] = useState("16:9");
  const [genAudio, setGenAudio] = useState(false);
  const [job, setJob] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const pollRef = useRef(null);

  const stopPolling = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };

  const reset = useCallback(() => { stopPolling(); setJob(null); setSubmitting(false); }, []);

  useEffect(() => () => stopPolling(), []);
  useEffect(() => { if (!open) reset(); }, [open, reset]);

  const poll = useCallback((videoId) => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const j = await getVideoJob(videoId);
        setJob(j);
        if (j.status === "completed" || j.status === "failed") {
          stopPolling();
          if (j.status === "failed") toast.error("Animation failed", { description: j.error || "Provider error" });
        }
      } catch {
        /* transient — keep polling */
      }
    }, 4000);
  }, []);

  const handleAnimate = async () => {
    setSubmitting(true);
    try {
      const j = await animateFrame({
        project_id: projectId,
        frame_id: frame.id,
        prompt: prompt.trim(),
        duration,
        aspect_ratio: aspect,
        generate_audio: genAudio,
      });
      setJob(j);
      poll(j.id);
    } catch (err) {
      toast.error("Could not start animation", { description: err.response?.data?.detail || err.message });
    } finally {
      setSubmitting(false);
    }
  };

  const working = job && (job.status === "queued" || job.status === "running");
  const done = job && job.status === "completed" && job.video_url;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[var(--bg-surface)] border-[var(--border-default)] max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-lg text-[var(--text-primary)] flex items-center gap-2">
            <Clapperboard size={18} className="text-[var(--color-gold-400)]" />
            Animate Shot
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 mt-2">
          {/* preview / result */}
          <div className="rounded-xl overflow-hidden bg-[var(--bg-overlay)] aspect-video flex items-center justify-center">
            {done ? (
              <video src={job.video_url} controls autoPlay loop className="w-full h-full object-contain" />
            ) : imageUrl ? (
              <img src={imageUrl} alt="frame" className="w-full h-full object-contain" />
            ) : (
              <Clapperboard size={40} className="text-[var(--text-disabled)]" />
            )}
          </div>

          {working ? (
            <div className="flex items-center justify-center gap-2 py-3 text-sm text-[var(--text-secondary)]">
              <Loader2 size={16} className="animate-spin text-[var(--color-gold-400)]" />
              {job.status === "queued" ? "Queued — generation takes a minute or two…" : "Rendering video…"}
            </div>
          ) : done ? (
            <div className="flex items-center gap-2">
              <a href={job.video_url} target="_blank" rel="noreferrer" className="flex-1">
                <Button variant="outline" className="w-full"><Download size={14} className="mr-1.5" /> Open / Download</Button>
              </a>
              <Button variant="ghost" onClick={reset}><RotateCcw size={14} className="mr-1.5" /> Redo</Button>
            </div>
          ) : (
            <>
              <Textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Motion direction (optional) — e.g. 'slow push in, eyes blink, subtle hair movement'"
                className="text-sm min-h-[60px] bg-[var(--bg-muted)] border-[var(--border-subtle)]"
              />
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <label className="text-[10px] text-[var(--text-muted)] uppercase tracking-wide">Aspect</label>
                  <div className="flex gap-1 mt-1">
                    {ASPECTS.map((a) => (
                      <button key={a} onClick={() => setAspect(a)}
                        className={`text-[10px] px-2 py-1 rounded border ${aspect === a ? "border-[var(--color-gold-500)] text-[var(--color-gold-400)]" : "border-[var(--border-subtle)] text-[var(--text-muted)]"}`}>{a}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-[10px] text-[var(--text-muted)] uppercase tracking-wide">Seconds</label>
                  <div className="flex gap-1 mt-1">
                    {DURATIONS.map((d) => (
                      <button key={d} onClick={() => setDuration(d)}
                        className={`text-[10px] px-2 py-1 rounded border ${duration === d ? "border-[var(--color-gold-500)] text-[var(--color-gold-400)]" : "border-[var(--border-subtle)] text-[var(--text-muted)]"}`}>{d}s</button>
                    ))}
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setGenAudio((v) => !v)}
                className="flex items-center justify-between w-full rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-muted)] px-3 py-2"
              >
                <span className="flex flex-col items-start">
                  <span className="text-xs text-[var(--text-secondary)]">Generate audio</span>
                  <span className="text-[10px] text-[var(--text-muted)]">Veo adds native sound & dialogue to the clip</span>
                </span>
                <span className={`relative inline-flex h-4 w-7 items-center rounded-full transition-colors ${genAudio ? "bg-[var(--color-gold-500)]" : "bg-[var(--border-default)]"}`}>
                  <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${genAudio ? "translate-x-3.5" : "translate-x-0.5"}`} />
                </span>
              </button>
              <Button className="btn-primary w-full" onClick={handleAnimate} disabled={submitting}>
                {submitting ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Clapperboard size={14} className="mr-1.5" />}
                Animate to video
              </Button>
              <p className="text-[10px] text-[var(--text-muted)] text-center">Image-to-video via Google Veo · keeps your frame as the first frame</p>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
